import {
  GSU_PRODUCT_ID,
  productIds,
  WSS_PRODUCT_ID,
} from "@/utils/helpers/mappings"
import { useContentJsx } from "@/utils/hooks/useContent"
import { usePys } from "@/utils/hooks/usePys"
import Notification from "@components/Notification/Notification"
import { Link } from "react-router-dom"
import { useSelector } from "react-redux"

import { selectProductNumbers } from "@/store/apis/linkApi"

interface ProtectYourSuperProps {
  memberProductIds?: any
}

/**
 * Widget component for displaying Protect Your Super (PYS) insurance alerts and information
 * @param memberProductIds - Optional array of member product IDs to filter insurance data for. If not provided, uses all available product numbers
 */
function ProtectYourSuperWidget({
  memberProductIds = [],
}: ProtectYourSuperProps) {
  const productNumbers = useSelector(selectProductNumbers)

  // Filter product numbers if memberProductIds are provided
  const filteredProductNumbers = memberProductIds.length
    ? productNumbers.filter((id) => memberProductIds.includes(id))
    : productNumbers

  // nonAffectedProductId is now provided by the usePys hook
  const { hasInsurances, specialFields, nonAffectedProductId } = usePys(
    filteredProductNumbers
  )

  // Build paths for content based on warnings and insurance status
  const paths = [
    ...(specialFields[GSU_PRODUCT_ID].data?.gsu_warning &&
    hasInsurances.includes(GSU_PRODUCT_ID)
      ? ["gsu"]
      : []),
    ...(specialFields[WSS_PRODUCT_ID].data?.wss_warning &&
    hasInsurances.includes(WSS_PRODUCT_ID)
      ? ["wss"]
      : []),
  ]

  const path = `protect_your_super.${paths.join("")}`
  const contentPayload = {
    ...specialFields[GSU_PRODUCT_ID].data,
    ...specialFields[WSS_PRODUCT_ID].data,
  }

  // Always call all hooks - use default values when paths are empty
  const title = useContentJsx({
    path: paths.length
      ? `${path}.alert_title`
      : "protect_your_super.gsu.alert_title",
    ...contentPayload,
  })

  const body = useContentJsx({
    path: paths.length
      ? `${path}.alert_content`
      : "protect_your_super.gsu.alert_content",
    ...contentPayload,
  })

  const productName = nonAffectedProductId
    ? productIds[nonAffectedProductId]
    : null
  const nonAffectedBody = useContentJsx({
    path: `protect_your_super.gsuwss.non_affected_account`,
    gsu_or_wss: productName || "",
  })

  // Early returns after all hooks have been called
  if (
    specialFields[GSU_PRODUCT_ID].isFetching ||
    specialFields[WSS_PRODUCT_ID].isFetching ||
    !hasInsurances.length ||
    !paths.length
  ) {
    return null
  }

  return (
    <div className="mol-component">
      <Notification notificationType={"bell-alert"} closeButton={true}>
        <h2>{title.contentBody}</h2>
        {body.contentBody}
        {nonAffectedProductId && nonAffectedBody?.contentBody && (
          <>{nonAffectedBody.contentBody}</>
        )}
        <p className="pull-right">
          <Link to="/insurance/keep-your-insurance" className="button">
            Keep your insurance
          </Link>
        </p>
      </Notification>
    </div>
  )
}

export default ProtectYourSuperWidget
